import React, { useEffect } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { ArrowRight, Mail } from "lucide-react";

import NavBar from "../components/common/navBar";
import Footer from "../components/common/footer";
import SectionHeader from "../components/common/sectionHeader";

import Hero from "../components/homepage/hero";
import Metrics from "../components/homepage/metrics";
import EngineeringIdentity from "../components/homepage/engineeringIdentity";
import ExperienceTimeline from "../components/homepage/experienceTimeline";
import AllProjects from "../components/projects/allProjects";
import SkillsGrid from "../components/homepage/skillsGrid";
import LearningRoadmap from "../components/homepage/learningRoadmap";
import JourneyTimeline from "../components/homepage/journeyTimeline";
import Article from "../components/articles/article";

import INFO from "../data/user";
import SEO from "../data/seo";
import myArticles from "../data/articles";

import "./styles/homepage.css";

const Homepage = () => {
	useEffect(() => {
		window.scrollTo(0, 0);
	}, []);

	const currentSEO = SEO.find((item) => item.page === "home") || SEO[0];

	// Get latest articles
	const sortedArticles = [...myArticles]
		.map((getArticle, index) => ({ ...getArticle(), id: index + 1 }))
		.sort((a, b) => new Date(b.date) - new Date(a.date))
		.slice(0, 2);

	return (
		<React.Fragment>
			<Helmet>
				<title>{INFO.main.title}</title>
				<meta name="description" content={currentSEO.description} />
				<meta name="keywords" content={currentSEO.keywords.join(", ")} />
			</Helmet>

			<div className="page-content">
				<NavBar active="home" />

				<main className="content-wrapper">
					<Hero />

					<Metrics />

					<EngineeringIdentity />

					<ExperienceTimeline />

					<section style={{ margin: "60px 0" }}>
						<SectionHeader
							tag="Featured Products"
							title="Highlighted"
							highlight="Engineering Projects"
							subtitle="Full-stack products built with Java, Spring Boot, PostgreSQL, React, and MongoDB."
						/>
						<AllProjects limit={3} />
						<div style={{ textAlign: "center", marginTop: "32px" }}>
							<Link to="/projects" className="btn-secondary">
								<span>Explore All Projects</span>
								<ArrowRight size={16} />
							</Link>
						</div>
					</section>

					<SkillsGrid />

					<LearningRoadmap />

					<JourneyTimeline />

					<section style={{ margin: "60px 0" }}>
						<SectionHeader
							tag="Technical Writing"
							title="Selected"
							highlight="Articles & Insights"
							subtitle="Long-form thoughts on database optimization, software evolution, and backend development."
						/>
						<div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "20px" }}>
							{sortedArticles.map((art, idx) => (
								<Article
									key={idx}
									date={art.date}
									title={art.title}
									description={art.description}
									link={`/article/${art.id}`}
								/>
							))}
						</div>
						<div style={{ textAlign: "center", marginTop: "24px" }}>
							<Link to="/articles" className="btn-secondary">
								<span>Read All Articles</span>
								<ArrowRight size={16} />
							</Link>
						</div>
					</section>

					<div className="contact-cta-banner">
						<h2 className="contact-cta-title">Let's build something useful.</h2>
						<p className="contact-cta-desc">
							Interested in software engineering opportunities, technical challenges, or discussions around backend systems, Spring Boot, and scalable products?
						</p>
						<Link to="/contact" className="btn-primary">
							<Mail size={16} />
							<span>Get In Touch</span>
						</Link>
					</div>

					<div className="page-footer">
						<Footer />
					</div>
				</main>
			</div>
		</React.Fragment>
	);
};

export default Homepage;
